import { forwardRef, type Ref } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import {
  getBaseListMarkerClassName,
  getBaseListSpacingClassName,
  resolveBaseAppearance,
} from './theme';
import type { BaseListItemProps, BaseListProps } from './types';
import { cn } from './utils';

export const BaseList = forwardRef<
  HTMLOListElement | HTMLUListElement,
  BaseListProps
>(function BaseList(
  {
    ordered = false,
    marker = 'auto',
    spacing = 'default',
    children,
    appearance,
    className,
    classNames,
    style,
    ...listProps
  },
  ref,
) {
  const resolvedAppearance = useMonkeysBaseAppearance(appearance);
  const componentAttributes = useMonkeysComponentAttributes();
  const theme = resolveBaseAppearance(resolvedAppearance);
  const sharedProps = {
    ...componentAttributes,
    ...listProps,
    'data-monkeys-component': 'list',
    'data-slot': 'root',
    'data-list-type': ordered ? 'ordered' : 'unordered',
    'data-marker': marker,
    'data-spacing': spacing,
    className: cn(
      theme.slots.listRoot,
      getBaseListSpacingClassName(theme, spacing),
      marker !== 'auto' && getBaseListMarkerClassName(theme, marker),
      classNames?.root,
      className,
    ),
    style: {
      ...theme.styles.list,
      ...style,
    },
    children,
  };

  return ordered ? (
    <ol {...sharedProps} ref={ref as Ref<HTMLOListElement>} />
  ) : (
    <ul {...sharedProps} ref={ref as Ref<HTMLUListElement>} />
  );
});

export const BaseListItem = forwardRef<HTMLLIElement, BaseListItemProps>(
  function BaseListItem(
    {
      children,
      appearance,
      className,
      classNames,
      style,
      ...itemProps
    },
    ref,
  ) {
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);

    return (
      <li
        {...componentAttributes}
        {...itemProps}
        ref={ref}
        data-monkeys-component="list-item"
        data-slot="item"
        className={cn(theme.slots.listItem, classNames?.root, className)}
        style={{
          ...theme.styles.listItem,
          ...style,
        }}
      >
        {children}
      </li>
    );
  },
);
